import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DollarSign, Users, TrendingUp, Shield } from 'lucide-react';
import { RevenueChart } from './RevenueChart';

export const OverviewTab = ({
  stats, donations,
  revenueSeries, revenueRange, setRevenueRange,
  paypal, integrations, rotation,
}) => {
  const { ppConfig, ppForm, setPpForm, ppShowSecret, setPpShowSecret, ppSaving, savePaypalConfig } = paypal;
  const { intConfig, intForm, setIntForm, intShowKey, setIntShowKey, intSaving, saveIntegrations } = integrations;
  const { rotating, rotationResult, rotateAdminPassword } = rotation;

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-tech p-6" data-testid="stat-total-revenue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-400 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold text-green-500 glow-text">${stats.total_revenue.toFixed(2)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-green-500/30" />
          </div>
        </Card>
        <Card className="card-tech p-6" data-testid="stat-basic-sales">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-400 text-sm">Basic Sales</p>
              <p className="text-3xl font-bold text-green-500">{stats.basic_sales}</p>
            </div>
            <Users className="w-10 h-10 text-green-500/30" />
          </div>
        </Card>
        <Card className="card-tech p-6" data-testid="stat-pro-sales">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-400 text-sm">Pro Sales</p>
              <p className="text-3xl font-bold text-green-500">{stats.pro_sales}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-500/30" />
          </div>
        </Card>
        <Card className="card-tech p-6" data-testid="stat-today-revenue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-400 text-sm">Today</p>
              <p className="text-3xl font-bold text-green-500">${stats.today_revenue.toFixed(2)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-green-500/30" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-tech p-6">
          <p className="text-green-400 text-sm mb-1">This Week</p>
          <p className="text-2xl font-bold text-green-500">${stats.week_revenue.toFixed(2)}</p>
        </Card>
        <Card className="card-tech p-6">
          <p className="text-green-400 text-sm mb-1">This Month</p>
          <p className="text-2xl font-bold text-green-500">${stats.month_revenue.toFixed(2)}</p>
        </Card>
        <Card className="card-tech p-6">
          <p className="text-green-400 text-sm mb-1">Total Users</p>
          <p className="text-2xl font-bold text-green-500">{stats.total_users}</p>
        </Card>
      </div>

      <RevenueChart revenueSeries={revenueSeries} revenueRange={revenueRange} setRevenueRange={setRevenueRange} />

      {/* Deploy & Source — one-click launch hub */}
      <Card className="card-tech p-6" data-testid="deploy-hub">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-green-400">🚀 Launch Hub</h3>
            <p className="text-green-500/70 text-sm">One-click access to deploy, source, payments & docs.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <a href="https://vercel.com/new" target="_blank" rel="noopener noreferrer"
            className="group block p-4 bg-black/60 border border-green-500/30 rounded-lg hover:border-green-400 hover:bg-green-500/10 transition-all"
            data-testid="deploy-vercel-btn">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-green-400" viewBox="0 0 76 65" fill="currentColor"><path d="M37.527.182l37.527 65H0z"/></svg>
              <span className="text-green-400 font-bold text-sm">Deploy on Vercel</span>
            </div>
            <p className="text-green-500/60 text-xs">Import your repo → set REACT_APP_BACKEND_URL → ship.</p>
          </a>
          <a href="https://github.com/new" target="_blank" rel="noopener noreferrer"
            className="group block p-4 bg-black/60 border border-green-500/30 rounded-lg hover:border-green-400 hover:bg-green-500/10 transition-all"
            data-testid="github-new-repo-btn">
            <div className="flex items-center gap-2 mb-1">
              <svg className="w-5 h-5 text-green-400" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
              <span className="text-green-400 font-bold text-sm">Create GitHub Repo</span>
            </div>
            <p className="text-green-500/60 text-xs">In Emergent, click &quot;Save to GitHub&quot; in the chat bar.</p>
          </a>
          <a href="https://www.paypal.com/businessmanage/account/apiAccess" target="_blank" rel="noopener noreferrer"
            className="group block p-4 bg-black/60 border border-green-500/30 rounded-lg hover:border-green-400 hover:bg-green-500/10 transition-all"
            data-testid="paypal-live-btn">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-bold text-sm">PayPal Live Keys</span>
            </div>
            <p className="text-green-500/60 text-xs">Swap Sandbox → Live to start accepting real payments.</p>
          </a>
          <a href="https://railway.app/new" target="_blank" rel="noopener noreferrer"
            className="group block p-4 bg-black/60 border border-green-500/30 rounded-lg hover:border-green-400 hover:bg-green-500/10 transition-all"
            data-testid="deploy-backend-btn">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-bold text-sm">Deploy Backend</span>
            </div>
            <p className="text-green-500/60 text-xs">Railway / Render / Atlas. See DEPLOYMENT.md.</p>
          </a>
        </div>
        <div className="mt-4 pt-4 border-t border-green-500/20 text-center">
          <p className="text-green-500/60 text-xs">
            Step-by-step:&nbsp;
            <a href="https://github.com/search?q=millionaire-the-machine" target="_blank" rel="noopener noreferrer"
              className="text-green-400 underline hover:text-green-300" data-testid="deploy-docs-link">
              DEPLOYMENT.md
            </a>
            &nbsp;·&nbsp;
            <span className="text-green-400">Demo Mode: {stats.demo_mode ? '🟡 ON (test)' : '🟢 OFF (live)'}</span>
          </p>
        </div>
      </Card>

      {/* PayPal Runtime Config */}
      <Card className="card-tech p-6" data-testid="paypal-config-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-xl font-bold text-green-400">💳 PayPal API Keys</h3>
            <p className="text-green-500/70 text-sm">Update your Client ID / Secret without editing files or redeploying.</p>
          </div>
          {ppConfig && (
            <div className="text-xs">
              <span className={`px-2 py-1 rounded font-bold ${ppConfig.mode === 'live' ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'}`}>
                {ppConfig.mode === 'live' ? '🟢 LIVE' : '🟡 SANDBOX'}
              </span>
              <span className="ml-2 text-green-500/60">source: {ppConfig.source}</span>
            </div>
          )}
        </div>
        <form onSubmit={savePaypalConfig} className="space-y-4">
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">Mode</label>
            <select value={ppForm.mode} onChange={e => setPpForm({ ...ppForm, mode: e.target.value })}
              className="w-full bg-black/50 border border-green-500/30 text-green-300 rounded-md h-11 px-3" data-testid="paypal-mode-select">
              <option value="sandbox">Sandbox (test)</option>
              <option value="live">Live (real money)</option>
            </select>
          </div>
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Client ID
              {ppConfig?.client_id_masked && (
                <span className="ml-2 text-green-500/60 text-xs font-normal">current: <code>{ppConfig.client_id_masked}</code></span>
              )}
            </label>
            <Input type="text" value={ppForm.client_id} onChange={e => setPpForm({ ...ppForm, client_id: e.target.value })}
              placeholder="Leave blank to keep current" className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm"
              autoComplete="off" data-testid="paypal-client-id-input" />
          </div>
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Client Secret
              <span className="ml-2 text-green-500/60 text-xs font-normal">
                {ppConfig?.has_client_secret ? '(current secret is stored)' : '(no secret set)'}
              </span>
            </label>
            <div className="relative">
              <Input type={ppShowSecret ? 'text' : 'password'} value={ppForm.client_secret}
                onChange={e => setPpForm({ ...ppForm, client_secret: e.target.value })}
                placeholder="Leave blank to keep current" className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm pr-16"
                autoComplete="new-password" data-testid="paypal-client-secret-input" />
              <button type="button" onClick={() => setPpShowSecret(!ppShowSecret)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-green-400 text-xs px-2 py-1 hover:text-green-300"
                data-testid="paypal-toggle-secret">
                {ppShowSecret ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-2">
            <Button type="submit" disabled={ppSaving} className="btn-primary px-6 py-2" data-testid="paypal-save-btn">
              {ppSaving ? 'Saving...' : 'Save PayPal Config'}
            </Button>
            <p className="text-green-500/60 text-xs">
              Stored securely in database — no restart or redeploy needed. New payments use the new keys immediately.
            </p>
          </div>
        </form>
      </Card>

      {/* Integrations */}
      <Card className="card-tech p-6" data-testid="integrations-config-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-xl font-bold text-green-400">📧 Integrations</h3>
            <p className="text-green-500/70 text-sm">Resend email + Slack sale notifications + admin email for password rotation.</p>
          </div>
        </div>
        <form onSubmit={saveIntegrations} className="space-y-4">
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Resend API Key
              {intConfig?.resend?.has_key && (
                <span className="ml-2 text-green-500/60 text-xs font-normal">
                  current: <code>{intConfig.resend.key_masked}</code> · <em>{intConfig.resend.source}</em>
                </span>
              )}
            </label>
            <div className="relative">
              <Input type={intShowKey ? 'text' : 'password'} value={intForm.resend_api_key}
                onChange={e => setIntForm({ ...intForm, resend_api_key: e.target.value })}
                placeholder="re_... (leave blank to keep current)" className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm pr-16"
                autoComplete="new-password" data-testid="int-resend-key-input" />
              <button type="button" onClick={() => setIntShowKey(!intShowKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-green-400 text-xs px-2 py-1 hover:text-green-300">
                {intShowKey ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Sender Email
              {intConfig?.sender_email?.value && (
                <span className="ml-2 text-green-500/60 text-xs font-normal">
                  current: <code>{intConfig.sender_email.value}</code> · <em>{intConfig.sender_email.source}</em>
                </span>
              )}
            </label>
            <Input type="email" value={intForm.sender_email} onChange={e => setIntForm({ ...intForm, sender_email: e.target.value })}
              placeholder="noreply@yourdomain.com" className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm"
              data-testid="int-sender-email-input" />
          </div>
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Slack Webhook URL
              {intConfig?.slack?.has_webhook ? (
                <span className="ml-2 text-green-500/60 text-xs font-normal">
                  current: <code>{intConfig.slack.webhook_masked}</code> · <em>{intConfig.slack.source}</em>
                </span>
              ) : (
                <span className="ml-2 text-green-500/40 text-xs font-normal">(not set — no sale pings)</span>
              )}
            </label>
            <Input type="text" value={intForm.slack_webhook_url} onChange={e => setIntForm({ ...intForm, slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/..." className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm"
              data-testid="int-slack-webhook-input" />
          </div>
          <div>
            <label className="block text-green-400 text-sm font-semibold mb-1">
              Admin Email (password rotation destination)
              {intConfig?.admin_email?.value && (
                <span className="ml-2 text-green-500/60 text-xs font-normal">
                  current: <code>{intConfig.admin_email.value}</code> · <em>{intConfig.admin_email.source}</em>
                </span>
              )}
            </label>
            <Input type="email" value={intForm.admin_email} onChange={e => setIntForm({ ...intForm, admin_email: e.target.value })}
              placeholder="you@yourdomain.com" className="bg-black/50 border-green-500/30 text-green-300 h-11 font-mono text-sm"
              data-testid="int-admin-email-input" />
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-2">
            <Button type="submit" disabled={intSaving} className="btn-primary px-6 py-2" data-testid="int-save-btn">
              {intSaving ? 'Saving...' : 'Save Integrations'}
            </Button>
            <p className="text-green-500/60 text-xs">Applied instantly — no restart. Empty fields keep current values.</p>
          </div>
        </form>
      </Card>

      {/* Admin Password Rotation */}
      <Card className="card-tech p-6" data-testid="password-rotation-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-xl font-bold text-green-400">🔐 Rotate Admin Password</h3>
            <p className="text-green-500/70 text-sm">
              Generate a fresh random password. Stored hashed in Mongo (overrides the env password). Sent to your admin email via Resend.
            </p>
          </div>
          <Button onClick={rotateAdminPassword} disabled={rotating} className="btn-secondary" data-testid="rotate-password-btn">
            {rotating ? 'Rotating…' : 'Rotate Password Now'}
          </Button>
        </div>
        {rotationResult && (
          <div className="mt-3 p-4 border-2 border-yellow-500/60 bg-yellow-500/10 rounded" data-testid="rotation-result">
            <p className="text-yellow-400 font-bold text-sm mb-1">⚠️ Save this password NOW — it&apos;s shown once</p>
            <code className="block bg-black p-3 text-yellow-300 font-mono text-sm break-all rounded" data-testid="rotation-new-password">
              {rotationResult.new_password}
            </code>
            <p className="text-yellow-500/80 text-xs mt-2">
              {rotationResult.emailed
                ? `Also emailed to ${rotationResult.email_recipient}. Next login uses this password.`
                : 'Email delivery FAILED — set admin_email + Resend key above and try again to enable emailing.'}
            </p>
          </div>
        )}
      </Card>

      {/* Recent Transactions */}
      <Card className="card-tech p-6" data-testid="recent-transactions">
        <h3 className="text-xl font-bold text-green-400 mb-4">Recent Transactions</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-green-500/20">
                <th className="text-left text-green-400 py-3 px-2">Date</th>
                <th className="text-left text-green-400 py-3 px-2">Email</th>
                <th className="text-left text-green-400 py-3 px-2">Tier</th>
                <th className="text-right text-green-400 py-3 px-2">Amount</th>
                <th className="text-right text-green-400 py-3 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {donations.slice(0, 10).map((d, idx) => (
                <tr key={`${d.payment_id || d.id || d.timestamp}-${idx}`} className="border-b border-green-500/10" data-testid={`transaction-row-${idx}`}>
                  <td className="py-3 px-2 text-green-300 text-sm">{(d.timestamp || '').slice(0, 10)}</td>
                  <td className="py-3 px-2 text-green-300 text-sm">{d.email || 'N/A'}</td>
                  <td className="py-3 px-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${d.tier === 'pro' ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-green-500/10 text-green-500'}`}>
                      {(d.tier || 'basic').toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right text-green-300 font-mono">${(d.amount || 0).toFixed(2)}</td>
                  <td className="py-3 px-2 text-right"><span className="text-green-500 text-xs">{d.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {donations.length === 0 && (
            <p className="text-center text-green-500/50 py-8">No transactions yet. They&apos;ll appear here as payments come in.</p>
          )}
        </div>
      </Card>
    </div>
  );
};
